/**
 * Pure helpers for the pipeline board's in-memory model.
 *
 * The server (GET /files/board-state) is the single source of truth on load;
 * the page holds the working state in React while the user acts. Nothing here
 * touches browser storage.
 */

import type {
  BatchExtractionStatus,
  BatchExtractionStatusCategory,
  BatchExtractionStatusItem,
  BoardStateFile,
} from "@/services/api";
import type { KnownMapping } from "@/types";
import type { PipelineFileDqxState } from "@/lib/pipelineDqx";
import { aggregatePipelineDqxRuns } from "@/lib/pipelineDqx";

export const BATCH_STARTING_PLACEHOLDER_ID = "starting";
export const BATCH_STARTING_STATE_MESSAGE = "Starting…";

export interface BatchIngestionSessionFile {
  fileId: string;
  fileName: string;
  schemaId: string;
  /** Human schema name from board-state when available. */
  schemaName?: string | null;
  reportType?: string | null;
  allowedReportTypes?: string[];
  /** Provider the file was uploaded for; null/omit when unassigned. */
  providerId?: string | null;
  /** Uploader Entra oid (or legacy email) from board-state / upload. */
  uploadedBy?: string | null;
  /** kodex_files.created_at from board-state; drives card ordering. */
  createdAt?: string | null;
}

/** In-memory board model — built from board-state on load, then owned by React state. */
export interface BatchIngestionSession {
  batchId: string;
  /** Legacy default schema; per-file schemaId on files is authoritative. */
  schemaId?: string;
  startedAt: string;
  files: BatchIngestionSessionFile[];
  /** File IDs the user has completed mapping for in this batch. */
  mappedFileIds?: string[];
  /** File IDs the user has confirmed structure for (preview accepted). */
  structureConfirmedFileIds?: string[];
  /** Terminal batch status snapshot — kept until the user dismisses the queue. */
  completedBatchStatus?: BatchExtractionStatus | null;
  /** Saved mapping rows per file — used when returning to the pipeline board. */
  mappedFileSnapshots?: Record<string, { savedMappings: KnownMapping[] }>;
  /** Source row totals per file for pipeline card display. */
  fileRowCounts?: Record<string, number>;
  /** Per-file regionId → row count for multi-table DQ denominators. */
  fileRegionRowCounts?: Record<string, Record<string, number>>;
  /** Selected table counts per file for pipeline card display. */
  fileTableCounts?: Record<string, number>;
  /** Background DQX job state per mapped file on the pipeline board. */
  fileDqxState?: Record<string, PipelineFileDqxState>;
}

/** Terminal batch status for a lone pipeline-board file (no batch extraction API). */
export function createSingleFileBatchStatus(
  file: BatchIngestionSessionFile,
  category: BatchExtractionStatusCategory = "succeeded"
): BatchExtractionStatus {
  return {
    batchId: `single-${file.fileId}`,
    totalFiles: 1,
    pendingCount: category === "pending" ? 1 : 0,
    runningCount: category === "running" ? 1 : 0,
    succeededCount: category === "succeeded" ? 1 : 0,
    failedCount: category === "failed" ? 1 : 0,
    skippedCount: category === "skipped" ? 1 : 0,
    cancelledCount: category === "cancelled" ? 1 : 0,
    items: [
      {
        fileId: file.fileId,
        workflowRunId: null,
        triggered: category !== "pending",
        skipReason: null,
        triggerError: null,
        lifeCycleState: null,
        resultState: null,
        stateMessage: null,
        failedTaskKey: null,
        failedTaskMessage: null,
        statusCategory: category,
        statusError: null,
      },
    ],
  };
}

export function removeFileFromBatchStatus(
  status: BatchExtractionStatus,
  fileId: string
): BatchExtractionStatus | null {
  const items = status.items.filter((item) => item.fileId !== fileId);
  if (items.length === 0) {
    return null;
  }
  return { ...status, ...statusCounts(items), totalFiles: items.length, items };
}

export function getFirstUnmappedSucceededFileId(
  batchStatus: BatchExtractionStatus,
  mappedFileIds: string[]
): string | null {
  const mapped = new Set(mappedFileIds);
  for (const item of batchStatus.items) {
    if (item.statusCategory === "succeeded" && !mapped.has(item.fileId)) {
      return item.fileId;
    }
  }
  return null;
}

/** Count mapped files that succeeded extraction (ignores stale mapped IDs). */
export function getMappedSucceededCount(
  batchStatus: BatchExtractionStatus,
  mappedFileIds: string[]
): number {
  const succeededIds = new Set(
    batchStatus.items
      .filter((item) => item.statusCategory === "succeeded")
      .map((item) => item.fileId)
  );
  return mappedFileIds.filter((fileId) => succeededIds.has(fileId)).length;
}

export function isBatchIngestionActive(status: {
  pendingCount: number;
  runningCount: number;
}): boolean {
  return status.pendingCount + status.runningCount > 0;
}

function countItemsByCategory(
  items: BatchExtractionStatusItem[],
  category: BatchExtractionStatusCategory
): number {
  return items.filter((item) => item.statusCategory === category).length;
}

function statusCounts(items: BatchExtractionStatusItem[]) {
  return {
    pendingCount: countItemsByCategory(items, "pending"),
    runningCount: countItemsByCategory(items, "running"),
    succeededCount: countItemsByCategory(items, "succeeded"),
    failedCount: countItemsByCategory(items, "failed"),
    skippedCount: countItemsByCategory(items, "skipped"),
    cancelledCount: countItemsByCategory(items, "cancelled"),
  };
}

function boardFileToStatusItem(file: BoardStateFile): BatchExtractionStatusItem {
  return {
    fileId: file.fileId,
    workflowRunId: null,
    triggered: file.extractionCategory !== "pending",
    skipReason: null,
    triggerError: null,
    lifeCycleState: null,
    resultState: null,
    stateMessage: null,
    failedTaskKey: null,
    failedTaskMessage: null,
    statusCategory: file.extractionCategory,
    statusError: null,
  };
}

function dqxStateFromBoardFile(file: BoardStateFile): PipelineFileDqxState | null {
  if (!file.dqx) return null;
  const boardRuns = file.dqx.runs ?? [];
  if (boardRuns.length > 1) {
    return aggregatePipelineDqxRuns(
      boardRuns.map((run) => ({
        regionId: run.regionId ?? null,
        runId: run.runId,
        jobId: run.jobId ?? "",
        phase: run.complete ? ("complete" as const) : ("running" as const),
        rowsPassing: run.rowsPassing,
        rowsWithIssues: run.rowsWithIssues,
        totalRowsChecked: run.totalRowsChecked,
      }))
    );
  }
  return file.dqx.complete
    ? {
        phase: "complete",
        runId: file.dqx.runId,
        jobId: file.dqx.jobId ?? "",
        rowsPassing: file.dqx.rowsPassing,
        rowsWithIssues: file.dqx.rowsWithIssues,
        totalRowsChecked: file.dqx.totalRowsChecked,
        ...(boardRuns[0]
          ? {
              runs: [
                {
                  regionId: boardRuns[0].regionId ?? file.dqx.regionId ?? null,
                  runId: boardRuns[0].runId,
                  jobId: boardRuns[0].jobId ?? "",
                  phase: "complete" as const,
                  rowsPassing: boardRuns[0].rowsPassing,
                  rowsWithIssues: boardRuns[0].rowsWithIssues,
                  totalRowsChecked: boardRuns[0].totalRowsChecked,
                },
              ],
            }
          : {}),
      }
    : {
        phase: "running",
        runId: file.dqx.runId,
        jobId: file.dqx.jobId ?? "",
        message: null,
        ...(boardRuns[0]
          ? {
              runs: [
                {
                  regionId: boardRuns[0].regionId ?? file.dqx.regionId ?? null,
                  runId: boardRuns[0].runId,
                  jobId: boardRuns[0].jobId ?? "",
                  phase: "running" as const,
                },
              ],
            }
          : {}),
      };
}

/** True when resume should poll an existing Databricks DQ run. */
export function shouldResumeDqxPolling(state: PipelineFileDqxState): boolean {
  return state.phase === "running" && state.runId != null;
}

function batchStatusFromItems(
  batchId: string,
  items: BatchExtractionStatusItem[]
): BatchExtractionStatus {
  return { batchId, totalFiles: items.length, ...statusCounts(items), items };
}

/** Build the pipeline board model from scoped board-state rows. */
export function buildSessionFromBoardFiles(
  boardFiles: BoardStateFile[],
  options?: {
    startedAt?: string;
    mappedFileSnapshots?: BatchIngestionSession["mappedFileSnapshots"];
    batchId?: string;
    schemaId?: string;
  }
): BatchIngestionSession | null {
  if (boardFiles.length === 0) return null;

  const items = boardFiles.map(boardFileToStatusItem);
  const batchId =
    options?.batchId ?? boardFiles.find((f) => f.batchId)?.batchId ?? "restored-board";

  const fileDqxState: Record<string, PipelineFileDqxState> = {};
  const fileRowCounts: Record<string, number> = {};
  const fileTableCounts: Record<string, number> = {};
  for (const file of boardFiles) {
    if (file.rowCount != null) fileRowCounts[file.fileId] = file.rowCount;
    if (file.tableCount != null) fileTableCounts[file.fileId] = file.tableCount;
    const dqx = dqxStateFromBoardFile(file);
    if (dqx) fileDqxState[file.fileId] = dqx;
  }

  return {
    batchId,
    schemaId: options?.schemaId,
    startedAt: options?.startedAt ?? new Date().toISOString(),
    files: boardFiles.map((f) => ({
      fileId: f.fileId,
      fileName: f.fileName,
      schemaId: f.schemaId,
      schemaName: f.schemaName ?? null,
      reportType: f.reportType,
      providerId: f.providerId,
      uploadedBy: f.uploadedBy,
      createdAt: f.createdAt,
    })),
    mappedFileIds: boardFiles.filter((f) => f.mapped).map((f) => f.fileId),
    structureConfirmedFileIds: boardFiles.filter((f) => f.structureConfirmed).map((f) => f.fileId),
    completedBatchStatus: batchStatusFromItems(batchId, items),
    mappedFileSnapshots: options?.mappedFileSnapshots,
    fileRowCounts,
    fileTableCounts,
    fileDqxState,
  };
}

/** Merge pipeline extraction status when appending files (dedupes by fileId).
 *  When `replaceExistingFileIds` is true, incoming items overwrite matching fileIds
 *  (used when retrying failed/cancelled ingestions).
 */
export function mergeBatchExtractionStatuses(
  existing: BatchExtractionStatus | null | undefined,
  incoming: BatchExtractionStatus,
  options?: { primaryBatchId?: string; replaceExistingFileIds?: boolean }
): BatchExtractionStatus {
  const existingItems = existing?.items ?? [];
  let items: BatchExtractionStatusItem[];
  if (options?.replaceExistingFileIds) {
    const incomingById = new Map(incoming.items.map((item) => [item.fileId, item]));
    const kept = existingItems.filter((item) => !incomingById.has(item.fileId));
    items = [...kept, ...incoming.items];
  } else {
    const existingIds = new Set(existingItems.map((item) => item.fileId));
    const newItems = incoming.items.filter((item) => !existingIds.has(item.fileId));
    items = [...existingItems, ...newItems];
  }

  return {
    batchId: options?.primaryBatchId ?? existing?.batchId ?? incoming.batchId,
    totalFiles: items.length,
    ...statusCounts(items),
    items,
  };
}

/** Placeholder batch status shown after upload, before trigger API returns a batch_id. */
export function createStartingBatchStatus(
  files: BatchIngestionSessionFile[],
  batchId: string = BATCH_STARTING_PLACEHOLDER_ID
): BatchExtractionStatus {
  return {
    batchId,
    totalFiles: files.length,
    pendingCount: files.length,
    runningCount: 0,
    succeededCount: 0,
    failedCount: 0,
    skippedCount: 0,
    cancelledCount: 0,
    items: files.map((file) => ({
      fileId: file.fileId,
      workflowRunId: null,
      triggered: false,
      skipReason: null,
      triggerError: null,
      lifeCycleState: null,
      resultState: null,
      stateMessage: BATCH_STARTING_STATE_MESSAGE,
      failedTaskKey: null,
      failedTaskMessage: null,
      statusCategory: "pending",
      statusError: null,
    })),
  };
}

export function interruptibleSleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(new DOMException("Aborted", "AbortError"));
  }
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** Dedupe key for per-file, per-stage housekeeping (artifact cleanup). */
export function stageKey(fileId: string, stage: string): string {
  return `${fileId}:${stage}`;
}
